import React, { useLayoutEffect, useRef, useState } from 'react';
import { t } from '../../i18n';
import Wordmark from '../ui/Wordmark';
import SoundToggle from './SoundToggle';
import { useFormatCurrency } from '../../utils/currencyContext';
import { GameStatusBanner, PlayerCard } from './GameParts';
import { ActionArea, UtilityRow } from './ActionArea';
import GameMenu from './GameMenu';
import BlindsUpClock from './BlindsUpClock';
import { seatPresence } from '../../utils/playerLedger';
import { ACTION_SLOT_H, usesInSeatBet } from '../../utils/tableLayout';
import { isEligibleWinnerSeat } from '../../utils/tapWinner';

// 1-3 players: full-width rows. 4-10: a 2-column grid of dense rows, so a full
// table still fits without pinning the action area to the viewport bottom.
//
// `squeeze` (0-2) steps the density down on short screens so the whole game
// screen fits with no scrolling. It is chosen from the screen height and the
// player count only, never from game state, so it cannot change mid-hand.
function getPlayerListLayout(count, squeeze = 0) {
  if (count <= 3) return { mode: 'single', density: ['default', 'compact', 'dense'][squeeze] || 'dense' };
  return { mode: 'grid', density: squeeze >= 2 ? 'micro' : 'dense' };
}

// Pick the loosest squeeze level whose content fits the scroll area. The
// content height of each level is measured once (it depends on width and
// player count, not on screen height or game state) and cached, so a height
// change just re-picks from the cache; an unmeasured level gets probed.
// eslint-disable-next-line react-hooks/exhaustive-deps -- runs every render on purpose; it only sets state when the level changes
function useFitSqueeze(ref, innerRef, count) {
  const [squeeze, setSqueeze] = useState(0);
  const [, setVh] = useState(0);
  const cache = useRef({ key: '', h: {} });
  useLayoutEffect(() => {
    // Re-check whenever the screen or the content box actually resizes (the
    // window resize event can fire before layout has settled).
    let n = 0;
    const bump = () => setVh(++n);
    window.addEventListener('resize', bump);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null;
    if (ro && ref.current) ro.observe(ref.current);
    if (ro && innerRef.current) ro.observe(innerRef.current);
    return () => { window.removeEventListener('resize', bump); ro?.disconnect(); };
  }, [ref, innerRef]);
  useLayoutEffect(() => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    const key = `${count}-${el.clientWidth}-${window.innerWidth}`;
    if (cache.current.key !== key) cache.current = { key, h: {} };
    const h = cache.current.h;
    // Total screen height this level needs: its scroll content plus the
    // fixed header above it (which also tightens with squeeze).
    const screen = el.parentElement;
    const padTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
    const screenH = screen ? screen.clientHeight : window.innerHeight;
    h[squeeze] = inner.offsetHeight + padTop + (screenH - el.clientHeight);
    let target = 0;
    while (target < 2 && h[target] !== undefined && h[target] > screenH + 0.5) target += 1;
    if (target !== squeeze) setSqueeze(target);
  });
  return squeeze;
}

const PLAYER_LIST_GAP = {
  default: 'space-y-2',
};

// The original Chipless game screen: header, pot strip, a vertical player list,
// then the action band directly below the last card. Markup moved verbatim out
// of GameBoard.jsx — the only additions are the header Menu and the shared
// ActionArea / UtilityRow components, which are the same code the table
// layout renders.
//
// Layout anti-pattern (see Chipless.md): the action area is NOT pinned to the
// viewport bottom, and the player list is never `flex-1`. To fit more players,
// compress the list (2-column grid) — never reposition the actions.
export default function ListLayout(view) {
  const formatCurrency = useFormatCurrency();
  const {
    game, settings, players, playersArray, currentPlayerId, currentPlayer, hostId,
    currentStage, handNumber, totalPot, currentPots, toCall,
    winnerIds, waitingForHost, roundActions, soundMuted = false, onToggleSound,
    onOpenBalances, onOpenFeedback, onLeaveTable, onSitOut,
    layout, onToggleLayout,
    tapWinnerMode, pickedWinnerIds = [], onToggleWinnerSeat, isDeclaringWinner,
  } = view;

  const scrollRef = useRef(null);
  const innerRef = useRef(null);
  const squeeze = useFitSqueeze(scrollRef, innerRef, playersArray.length);
  const tight = squeeze > 0;
  const { mode: playerListMode, density: playerDensity } = getPlayerListLayout(playersArray.length, squeeze);

  return (
    <>
      {/* Header */}
      <div className={`px-[22px] ${tight ? 'pt-3' : 'pt-4'} flex-shrink-0`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Wordmark size="sm" />
            {onToggleSound && <SoundToggle muted={soundMuted} onToggle={onToggleSound} />}
          </div>
          <div className="flex items-center gap-2.5 min-w-0">
            <BlindsUpClock settings={settings} />
            <span data-testid="hand-number" className="text-[11px] text-mute tracking-[0.09em] uppercase whitespace-nowrap">
              {t('game.handNumber', { n: handNumber })}
            </span>
            <GameMenu
              layout={layout}
              onToggleLayout={onToggleLayout}
              onOpenBalances={onOpenBalances}
              onOpenFeedback={onOpenFeedback}
              onLeaveTable={onLeaveTable}
              onSitOut={onSitOut}
              roundActions={roundActions}
            />
          </div>
        </div>

        <div className={`${tight ? 'mt-1.5' : 'mt-2.5'} flex items-center justify-between`}>
          <div className="flex gap-1.5" data-testid="board-cards">
            {[0,1,2,3,4].map(i => {
              const revealed = i < ({ FLOP: 3, TURN: 4, RIVER: 5, SHOWDOWN: 5 }[currentStage] || 0);
              return (
                <div
                  key={i}
                  style={{ width: 22, height: 30 }}
                  className={`rounded-[3px] border ${revealed ? 'bg-ink border-ink' : 'bg-transparent border-line'}`}
                />
              );
            })}
          </div>
        </div>

        {/* Status banner — one place that always says whose move it is and what's next */}
        <GameStatusBanner
          game={game}
          players={players || {}}
          viewerId={currentPlayerId}
          hostId={hostId}
          tapWinnerMode={tapWinnerMode}
        />

        {/* Pot hero strip */}
        <div className={`${tight ? 'mt-2.5 py-2.5' : 'mt-[14px] py-4'} border-t border-b border-line flex items-baseline justify-between`}>
          {/* Left: Pot */}
          <div>
            <div className="flex items-center gap-2 label-eyebrow mb-1 h-[14px]">
              <span>{t('poker.pot')}</span>
              {(currentPots?.length ?? 0) > 1 && (
                <span className="text-[9.5px] font-semibold tracking-[0.08em] px-[6px] py-[2px] rounded-[4px] border border-line text-mute bg-cream">
                  {currentPots.length} {t('poker.potsCaps')}
                </span>
              )}
            </div>
            <div className={`${tight ? 'text-[32px]' : 'text-[38px]'} font-medium tracking-[-0.035em] tnum leading-none`} data-testid="pot-total">
              {formatCurrency(totalPot)}
            </div>
          </div>
          {/* Right: To call. The side-pot split hangs below it, absolutely
              positioned, so a side pot appearing never changes the strip's
              height (everything below would shift). */}
          <div className="text-right relative">
            <div className="label-eyebrow mb-1 h-[14px]">{t('table.toCall')}</div>
            <div className="text-[22px] font-medium tracking-[-0.025em] tnum leading-none">
              {formatCurrency(toCall)}
            </div>
            {(currentPots?.length ?? 0) > 1 && (
              <div className="absolute right-0 top-full mt-1 text-[11px] text-mute tnum whitespace-nowrap leading-none">
                {t('table.main')} {formatCurrency(currentPots.find(p => p.isMainPot)?.amount ?? currentPots[0]?.amount ?? 0)}
                <span className="text-line mx-1">·</span>
                {t('table.side')} {formatCurrency(currentPots.filter(p => !p.isMainPot).reduce((s, p) => s + (p.amount ?? 0), 0))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable content — actions sit directly below player cards */}
      <div ref={scrollRef} data-squeeze={squeeze} className={`flex-1 min-h-0 overflow-y-auto px-[22px] ${tight ? 'pt-2.5' : 'pt-3.5'}`}>
        <div ref={innerRef}>
        {/* Players */}
        <div className={playerListMode === 'grid' ? 'grid grid-cols-2 gap-x-2 gap-y-1' : PLAYER_LIST_GAP.default}>
          {playersArray.map((player) => {
            const picked = tapWinnerMode && pickedWinnerIds.includes(player.id);
            const pickable = tapWinnerMode && !isDeclaringWinner && isEligibleWinnerSeat(player);
            return (
              <PlayerCard
                key={player.id}
                player={player}
                layout={playerListMode}
                density={playerDensity}
                isCurrentTurn={game?.currentTurnPlayerId === player.id && seatPresence(player) !== 'away' && seatPresence(player) !== 'next'}
                isWinner={(winnerIds.includes(player.id) || picked) && !player.hasFolded && seatPresence(player) !== 'away' && seatPresence(player) !== 'next'}
                isHostSeat={player.id === hostId}
                inSeatBet={usesInSeatBet(playersArray.length)}
                roundActions={roundActions}
                stage={game?.stage}
                picked={picked}
                onPick={pickable ? () => onToggleWinnerSeat?.(player.id) : undefined}
                handAwarded={winnerIds.length > 0}
                winnerInfo={game?.winnerInfo}
              />
            );
          })}
        </div>

        {/* Action area — directly below player list. The action slot is a
            constant height so nothing below the player list moves between
            states. History lives in the Menu. */}
        <div className={tight ? 'mt-2 pb-2' : 'mt-3 pb-6'}>
          <div
            data-testid="list-action-slot"
            className="flex flex-col justify-center gap-2"
            style={{ minHeight: ACTION_SLOT_H }}
          >
            {/* Waiting for host — shown after this player dismissed the winner
                modal, until the host deals the next hand (winnerInfo cleared) */}
            {waitingForHost && (currentPlayer?.stack || 0) > 0 && (
              <div className="flex items-center justify-center gap-2">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8a857d', display: 'inline-block', flexShrink: 0, animation: 'cl-pulse 1.6s ease-in-out infinite' }} />
                <span className="text-[12.5px] text-mute">{t('table.waitingDealNext')}</span>
              </div>
            )}
            <ActionArea {...view} />
          </div>
          <UtilityRow {...view} tight={tight} />
      </div>
        </div>

      </div>
    </>
  );
}
